// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {PurchaseDisabledWrapper} from '@app/features/app/components/dialogs/components/plutonium/PurchaseDisabledWrapper';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PAYMENT_PROVIDER_NAME, PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {BoostGemIcon} from '@app/features/boost/components/BoostGemIcon';
import {BoostQuantityStep} from '@app/features/boost/components/BoostQuantityStep';
import GuildBoosts from '@app/features/boost/state/GuildBoosts';
import {BOOST_THIS_COMMUNITY_DESCRIPTOR} from '@app/features/boost/utils/GuildBoostMessages';
import {liveBoughtSlotCount} from '@app/features/boost/utils/GuildBoostPurchase';
import type {CosmeticType} from '@app/features/cosmetics/state/Cosmetics';
import {COSMETIC_TYPE_LABELS} from '@app/features/cosmetics/utils/CosmeticLabels';
import {cosmeticName} from '@app/features/cosmetics/utils/CosmeticText';
import {
	CANCEL_DESCRIPTOR,
	CLOSE_DESCRIPTOR,
	CONTINUE_DESCRIPTOR,
	MINUTES_DURATION_PLURAL_DESCRIPTOR,
	OKAY_DESCRIPTOR,
	TRY_AGAIN_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {getCachedNumberFormat} from '@app/features/i18n/utils/IntlCache';
import {planSavingsPercent, recurringSavingsPercent} from '@app/features/premium/utils/PremiumPlanSavings';
import {holdsPlusSubscription} from '@app/features/premium/utils/PremiumSubscriptionHold';
import {ShopBundleDetailModal} from '@app/features/shop/components/modals/ShopBundleDetailModal';
import styles from '@app/features/shop/components/modals/ShopCheckoutModal.module.css';
import {ShopItemDetailModal} from '@app/features/shop/components/modals/ShopItemDetailModal';
import {UsernameCardNameStep} from '@app/features/shop/components/modals/UsernameCardNameStep';
import {ShopCurrencyNote} from '@app/features/shop/components/ShopCurrencyNote';
import {ShopEquippedSuccess} from '@app/features/shop/components/ShopEquippedSuccess';
import {ShopItemStage} from '@app/features/shop/components/ShopItemStage';
import {ShopPrice} from '@app/features/shop/components/ShopPrice';
import {ShopRecurringReason} from '@app/features/shop/components/ShopRecurringReason';
import {UsernameCardArt} from '@app/features/shop/components/UsernameCardArt';
import {type ShopCheckoutFlow, useShopCheckout} from '@app/features/shop/hooks/useShopCheckout';
import {
	type UsernameCardAvailability,
	useUsernameCardAvailability,
} from '@app/features/shop/hooks/useUsernameCardAvailability';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {type ShopPackageEntry, usernameSku} from '@app/features/shop/utils/ShopEntries';
import {purchaseBlockOf} from '@app/features/shop/utils/ShopItemAction';
import {
	ALREADY_OWNED_DESCRIPTOR,
	BOOST_LIMIT_DESCRIPTOR,
	BOOST_LIMIT_TITLE_DESCRIPTOR,
	BOOST_SLOT_NAME_DESCRIPTOR,
	BOOST_SLOT_NOTE_DESCRIPTOR,
	BUNDLE_CONTENTS_CHANGED_DESCRIPTOR,
	BUNDLE_INCLUDES_DESCRIPTOR,
	BUNDLE_ITEM_OWNED_DESCRIPTOR,
	BUY_BOOST_DESCRIPTOR,
	BUY_ITEM_DESCRIPTOR,
	BUY_PREMIUM_DESCRIPTOR,
	CANT_BE_BOUGHT_RIGHT_NOW_DESCRIPTOR,
	CHANGE_NAME_DESCRIPTOR,
	CHECK_AGAIN_DESCRIPTOR,
	CHOOSE_A_PLAN_DESCRIPTOR,
	CLAIM_ACCOUNT_TO_BUY_DESCRIPTOR,
	CONTINUE_AT_NEW_PRICE_DESCRIPTOR,
	EQUIP_DESCRIPTOR,
	FINISH_PAYING_IN_BROWSER_DESCRIPTOR,
	FINISH_PAYING_IN_WINDOW_DESCRIPTOR,
	GIVING_YOU_NAME_DESCRIPTOR,
	HELD_FOR_YOU_UNTIL_DESCRIPTOR,
	HOLD_AND_PAY_DESCRIPTOR,
	HOLD_NAME_FOR_YOU_DESCRIPTOR,
	LIFETIME_MEANS_DESCRIPTOR,
	LOADING_PRICE_DESCRIPTOR,
	NAME_BECOMES_YOURS_DESCRIPTOR,
	NAME_IS_ALREADY_YOURS_DESCRIPTOR,
	ONE_TIME_PAYMENT_DESCRIPTOR,
	OPEN_PAYMENT_PAGE_DESCRIPTOR,
	ORDER_MISMATCH_DESCRIPTOR,
	ORDER_NUMBER_DESCRIPTOR,
	PACKAGE_MONTHS_DESCRIPTOR,
	PAY_WITHIN_5_MINUTES_DESCRIPTOR,
	PAYMENT_NOT_APPROVED_DESCRIPTOR,
	PAYMENT_NOT_COMPLETED_DESCRIPTOR,
	PAYMENT_NOT_CONFIRMED_YET_DESCRIPTOR,
	PAYMENT_PAGE_LANGUAGE_DESCRIPTOR,
	PAYMENT_PAGE_OPENED_DESCRIPTOR,
	PAYMENT_TIMEOUT_DESCRIPTOR,
	PAYMENT_WINDOW_OPEN_DESCRIPTOR,
	PREMIUM_DISCOUNT_PERCENT_DESCRIPTOR,
	PREMIUM_PRICE_DESCRIPTOR,
	PRICE_CHANGED_DESCRIPTOR,
	PRICE_DESCRIPTOR,
	RATE_LIMITED_WAIT_DESCRIPTOR,
	RECURRING_CONSENT_DESCRIPTOR,
	RECURRING_QUOTE_CHANGED_DESCRIPTOR,
	RECURRING_RENEWAL_NOTE_DESCRIPTOR,
	RECURRING_TOTAL_DESCRIPTOR,
	RECURRING_UNAVAILABLE_DESCRIPTOR,
	REMAINING_TIME_KEPT_DESCRIPTOR,
	SALE_PERCENT_DESCRIPTOR,
	SALES_PAUSED_DESCRIPTOR,
	SETTING_UP_USERNAME_DESCRIPTOR,
	SHORT_NAME_RELEASED_LATER_DESCRIPTOR,
	SOLD_OUT_DESCRIPTOR,
	SUBSCRIBE_DESCRIPTOR,
	SUBSCRIPTION_BLOCKS_ONE_TIME_DESCRIPTOR,
	TAKE_IT_FREE_DESCRIPTOR,
	TAKE_NAME_FREE_TITLE_DESCRIPTOR,
	TIME_ADDED_ON_TOP_DESCRIPTOR,
	TOO_MANY_ORDERS_DESCRIPTOR,
	TOTAL_DESCRIPTOR,
	TRY_AGAIN_IN_A_FEW_SECONDS_DESCRIPTOR,
	TRY_FREE_FOR_DAYS_DESCRIPTOR,
	USERNAME_CARD_PERMIT_DESCRIPTOR,
	USERNAME_CARD_TITLE_DESCRIPTOR,
	USERNAME_OFFER_UNTIL_DESCRIPTOR,
	VERIFY_EMAIL_TO_BUY_DESCRIPTOR,
	VISIONARY_NAME_DESCRIPTOR,
	VISIONARY_NAME_RIGHT_TITLE_DESCRIPTOR,
	VISIONARY_REWARDS_DESCRIPTOR,
	VISIONARY_RIGHT_ONCE_DESCRIPTOR,
	VISIONARY_RIGHT_UNAVAILABLE_DESCRIPTOR,
	VISIONARY_SOLD_OUT_DESCRIPTOR,
	WAITING_FOR_PAYMENT_DESCRIPTOR,
} from '@app/features/shop/utils/ShopMessageDescriptors';
import {
	campaignShown,
	chargeMinor,
	chargePayment,
	formatAzn,
	formatDiscount,
	formatPayable,
	formatRecurringAmount,
	formatTry,
	percentOff,
	priceCurrency,
	usdCentsToAznMinor,
	usdToAznRateMicro,
} from '@app/features/shop/utils/ShopPriceFormat';
import {Button} from '@app/features/ui/button/Button';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {CopyButton} from '@app/features/ui/components/CopyButton';
import {Spinner} from '@app/features/ui/components/Spinner';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import {UserSettingsModal} from '@app/features/user/components/modals/UserSettingsModal';
import Users from '@app/features/user/state/Users';
import {getFormattedFullDate, getFormattedTime} from '@app/features/user/utils/DateFormatting';
import {DEFAULT_FREE_LIMITS} from '@fluxer/limits/src/LimitDefaults';
import type {CosmeticItemResponse} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {
	PADDLE_BOOST_MAX_QUANTITY,
	type RecurringQuote,
	type ShopBillingMode,
} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {ShopViewerPayment} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import type {
	ShopOrderResponse,
	ShopPriceResponse,
	ShopQuoteResponse,
} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon, AtIcon, CrownIcon, StorefrontIcon, WarningIcon, XCircleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

// A community's boost page that opened the dialog: it reads its slots again once the slot is delivered, and the
// success view puts the new slot on that community.
export interface ShopBoostHere {
	refresh: () => void;
	ready: () => boolean;
	boost: () => Promise<boolean>;
}

// A monthly boost subscription instead of one 30 day slot: how many boosts it starts with, and the most it may hold.
export interface ShopBoostSubscription {
	quantity: number;
	maxQuantity: number;
}

// fromItem: a premium-included item that led here; the dialog previews it instead of the crown.
export type ShopCheckoutTarget =
	| {kind: 'item'; sku: string}
	| {kind: 'premium'; months: 1 | 3 | 12; billingMode?: ShopBillingMode; fromItem?: CosmeticItemResponse}
	| {kind: 'visionary'}
	| {kind: 'bundle'; sku: string}
	| {kind: 'boost'; here?: ShopBoostHere; recurring?: ShopBoostSubscription}
	// name: a named username or a personal offer, sold as that one name (offer: the offer's code, offerEndsAt: until
	// when it is sold); visionary: opened to take a name free with the Visionary username right, so nothing is priced.
	| {
			kind: 'username';
			length: 1 | 2 | 3;
			name?: string;
			offer?: string;
			offerEndsAt?: string;
			visionary?: boolean;
	  };

interface ShopCheckoutModalProps {
	target: ShopCheckoutTarget;
}

// Existing texts, reused as they are.
const PURCHASE_DESCRIPTOR = msg({message: 'Purchase', comment: 'Buy button in the shop buy dialog.'});
const PROFILE_DECORATIONS_DESCRIPTOR = msg({
	message: 'Profile decorations',
	comment: 'Button after buying an item in the shop; opens the profile decorations settings.',
});
const LOCKED_DESCRIPTOR = msg({
	message: '{name} comes with {premiumProductName}.',
	comment: 'Shop buy dialog for Plus, opened from a Plus-only decoration. {name} is the decoration name.',
});
const COPY_DESCRIPTOR = msg({message: 'Copy', comment: 'Copies the order number in the shop buy dialog.'});
const PAYMENT_PRICE_NOTE_DESCRIPTOR = msg({
	message: 'The payment page will show Azerbaijani manat: {amount} AZN (store price {price}).',
	comment: 'Shown before opening Payriff. Both prices come from the persisted order quote.',
});
const PADDLE_PRICE_NOTE_DESCRIPTOR = msg({
	message: 'The payment page will show {amount}.',
	comment:
		'Shown before opening the Paddle payment page. {amount} is the charge with its currency, such as $4.99 or 199 TL.',
});
const PADDLE_NAME = 'Paddle';
const CHECKOUT_PADDLE_NOTE_DESCRIPTOR = msg({
	message: 'Payment is processed by Paddle.com, our Merchant of Record.',
	comment:
		'Small line in the buy dialog where Paddle sells. Keep "Paddle.com" and "Merchant of Record": Paddle has to be named as the Merchant of Record. The currency shows in the price.',
});
const CHECKOUT_PAYRIFF_AZN_NOTE_DESCRIPTOR = msg({
	message: 'Micup is an Azerbaijani company; Payriff collects the payment in Azerbaijani manat (AZN).',
	comment: 'Small line in the buy dialog where Payriff sells in manat. Keep the company location explicit.',
});
const TAXES_INCLUDED_DESCRIPTOR = msg({
	message: 'Taxes included',
	comment: 'Small grey line under the big price in the buy dialog where Paddle sells: the price includes taxes.',
});
const PER_PERIOD_DESCRIPTOR = msg({
	message: '/ {months, plural, =12 {year} one {month} other {# months}}',
	comment: 'After the big price of a renewing Micup Plus plan in the buy dialog, e.g. "₺949,99 / year".',
});
const SAVE_PERCENT_DESCRIPTOR = msg({
	message: 'Save {percent}',
	comment:
		'Badge on a Plus plan in the buy dialog: what a month of this plan saves against the one month plan. {percent} is a formatted percentage such as 16%.',
});

const BOOST_SUBSCRIPTION_TITLE_DESCRIPTOR = msg({
	message: 'Boost subscription',
	comment: 'Title of the dialog that starts a monthly boost subscription with a chosen number of boosts.',
});
const CHOOSE_QUANTITY_STEP_DESCRIPTOR = msg({
	message: 'Choose how many',
	comment: 'First step name at the top of the boost subscription dialog: picking the number of boosts.',
});
const PAYMENT_STEP_DESCRIPTOR = msg({
	message: 'Payment',
	comment: 'Second step name at the top of the boost subscription dialog: paying on the Paddle page.',
});
const BOOST_RECURRING_CONSENT_DESCRIPTOR = msg({
	message: 'I agree that my boosts renew automatically and {amount} is charged every month until I cancel.',
	comment:
		'Required checkbox in the boost subscription dialog before the order opens. {amount} is the monthly total of all chosen boosts. Legal-effect text.',
});
const BOOST_RENEWAL_NOTE_DESCRIPTOR = msg({
	message:
		"Boosts are a renewing subscription: {amount} is charged every month until you cancel. Cancel any time on a community's Boost page; it takes effect at the end of the paid period.",
	comment:
		'Note in the boost subscription dialog. {amount} is the monthly total. Legal-effect text: keep the amount, the period, automatic charging and how cancelling works.',
});
const BOOST_ONE_TIME_NOTE_DESCRIPTOR = msg({
	message: 'One-time payment. Each boost lasts 30 days and does not renew.',
	comment: 'Note in the one-time boost buy dialog with a quantity picker: one payment, every boost a 30 day slot.',
});
const BOOST_SUBSCRIPTION_UNAVAILABLE_DESCRIPTOR = msg({
	message: "A boost subscription can't be started right now.",
	comment: 'Shown in the boost subscription dialog when the server does not offer one to this account now.',
});

const EASE_OUT = [0.23, 1, 0.32, 1] as const;
// An unpaid username order holds its name this long from when it opened, for an older API without username_held_until.
const USERNAME_HOLD_MS = 5 * 60 * 1000;

function nameHoldEnd(order: ShopOrderResponse): number {
	return order.username_held_until
		? Date.parse(order.username_held_until)
		: Date.parse(order.created_at) + USERNAME_HOLD_MS;
}

// A username order past its own minutes is no longer offered to pay; one timer draws the dialog again when they end.
function useNameHoldOver(order: ShopOrderResponse | null): boolean {
	const end = order?.username ? nameHoldEnd(order) : null;
	const [, setTick] = useState(0);
	useEffect(() => {
		if (end === null || end <= Date.now()) return;
		const timer = window.setTimeout(() => setTick((tick) => tick + 1), end - Date.now());
		return () => window.clearTimeout(timer);
	}, [end]);
	return end !== null && Date.now() >= end;
}

type PriceParts = Pick<
	ShopQuoteResponse,
	| 'provider'
	| 'currency'
	| 'base_usd_cents'
	| 'campaign_percent'
	| 'premium_percent'
	| 'final_usd_cents'
	| 'amount_azn_minor'
	| 'usd_to_azn_rate_micro'
	| 'usd_to_azn_rate'
	| 'charge_amount_minor'
	| 'list_payable'
>;

// The campaign step rounds half up as the server does. Summary rows subtract the converted cumulative levels,
// including the authoritative final amount, rather than converting USD discounts independently.
function saleCents(price: PriceParts): number {
	if (price.premium_percent === 0) return price.final_usd_cents;
	return percentOff(price.base_usd_cents, price.campaign_percent);
}

// The name step showed the check's price of the name; the confirm step's quote is what the order is priced at. A
// server that names payable amounts is compared in the charged currency, an older one in US cents.
function namePriceMoved(shown: ShopPriceResponse | null, quote: ShopQuoteResponse | null): boolean {
	if (!shown || !quote) return false;
	return shown.payable
		? shown.payable.currency !== quote.currency || shown.payable.amount_minor !== chargeMinor(quote)
		: shown.sale_usd_cents !== quote.final_usd_cents;
}

// The server's discount steps on the admin's lira list price, as computeShopPrice takes them.
function liraPayable(tryMinor: number, parts: Pick<PriceParts, 'campaign_percent' | 'premium_percent'>): number {
	return percentOff(percentOff(tryMinor, parts.campaign_percent), parts.premium_percent);
}

const purchaseBlockDescriptor = (block: 'unclaimed' | 'unverified' | null) =>
	block === 'unclaimed' ? CLAIM_ACCOUNT_TO_BUY_DESCRIPTOR : VERIFY_EMAIL_TO_BUY_DESCRIPTOR;

// Takes focus when a new state appears.
const focusOnMount = (node: HTMLHeadingElement | null) => node?.focus();

// Rows from the server's numbers; `before` marks what differs from the price shown earlier (price_changed).
interface SummaryProps {
	price: PriceParts;
	before?: PriceParts;
	order?: ShopOrderResponse;
	// The buy view: the total once and big, after the renewal period; the list price only above a discount.
	hero?: boolean;
	period?: number | null;
	// Several one-time boosts: every row is this many of the quote's price of one, as the order charges them.
	quantity?: number;
}

const Summary = observer(({price, before, order, hero = false, period = null, quantity = 1}: SummaryProps) => {
	const {i18n} = useLingui();
	const payment = chargePayment(price, ShopCatalog.payment);
	// A Paddle USD charge is the USD price itself; every other one goes through AZN.
	const usd = payment.provider === 'paddle' && payment.currency === 'USD';
	// A server that names the payable list price prices every row in the charged currency. An order names none: where
	// the price shown before had one, the order's rows in TL or AZN would come from a stale conversion, so only its
	// charge shows.
	const totalOnly = !price.list_payable && !usd && before?.list_payable != null;
	// Payriff in TL: the rows are the admin's lira price and its discounts; Payriff charges the quote's manat.
	const lira = price.list_payable?.try_minor != null;
	const rowCurrency = lira ? 'TRY' : (price.list_payable?.currency ?? (totalOnly ? price.currency : null));
	// Manat rows show as Payriff shows them: in TL where the viewer sees TL, the manat to pay under the total.
	const ownRows = (rowCurrency !== null && rowCurrency !== 'AZN') || usd;
	const percent = (value: number) => formatDiscount(value, i18n.locale);
	const rowsOf = (parts: PriceParts) => {
		const list = parts.list_payable;
		if (totalOnly && !list) {
			return [{key: 'total', label: i18n._(TOTAL_DESCRIPTOR), amount: chargeMinor(parts), subtract: 0}];
		}
		const rate = usdToAznRateMicro(parts);
		const liraTotal = list?.try_minor != null ? liraPayable(list.try_minor, parts) : null;
		const total = liraTotal ?? (list ? chargeMinor(parts) : usd ? parts.final_usd_cents : parts.amount_azn_minor);
		const base = list
			? (list.try_minor ?? list.amount_minor)
			: usd
				? parts.base_usd_cents
				: rate == null
					? null
					: usdCentsToAznMinor(parts.base_usd_cents, rate);
		const sale =
			parts.premium_percent === 0
				? total
				: list
					? percentOff(list.try_minor ?? list.amount_minor, parts.campaign_percent)
					: usd
						? saleCents(parts)
						: rate == null
							? null
							: usdCentsToAznMinor(saleCents(parts), rate);
		const rows = [{key: 'price', label: i18n._(PRICE_DESCRIPTOR), amount: base, subtract: 0}];
		if (parts.campaign_percent > 0) {
			const label = i18n._(SALE_PERCENT_DESCRIPTOR, {percent: percent(parts.campaign_percent)});
			rows.push({key: 'sale', label, amount: base, subtract: sale ?? 0});
		}
		if (parts.premium_percent > 0) {
			const label = i18n._(PREMIUM_DISCOUNT_PERCENT_DESCRIPTOR, {
				premiumProductName: PREMIUM_PRODUCT_NAME,
				percent: percent(parts.premium_percent),
			});
			rows.push({key: 'premium', label, amount: sale, subtract: total});
		}
		rows.push({key: 'total', label: i18n._(TOTAL_DESCRIPTOR), amount: total, subtract: 0});
		return rows;
	};
	// Rows in lira and rows in manat are different numbers: such an earlier price marks nothing changed.
	const unitsOf = (parts: PriceParts) =>
		parts.list_payable?.try_minor != null ? 'TRY' : (parts.list_payable?.currency ?? parts.currency);
	const earlier =
		before && unitsOf(before) === unitsOf(price)
			? new Map(rowsOf(before).map((row) => [row.key, row.amount === null ? null : row.amount - row.subtract]))
			: null;
	const rows = rowsOf(price).map((row) => ({
		...row,
		amount: row.amount === null ? null : row.amount * quantity,
		subtract: row.subtract * quantity,
	}));
	return (
		<dl
			className={clsx(styles.summary, hero && styles.hero)}
			aria-live="polite"
			data-flx="shop.shop-checkout-modal.summary"
		>
			{(hero && rows.length === 2 ? rows.slice(1) : rows).map((row) => (
				<div
					key={row.key}
					className={clsx(
						styles.row,
						row.key === 'total' && (hero ? styles.heroTotal : styles.totalRow),
						earlier &&
							earlier.get(row.key) !== (row.amount === null ? null : row.amount - row.subtract) &&
							styles.changed,
					)}
					data-flx="shop.shop-checkout-modal.row"
				>
					<dt
						className={clsx(hero && row.key === 'total' && styles.srOnly)}
						data-flx="shop.shop-checkout-modal.row-label"
					>
						{row.label}
					</dt>
					<dd data-flx="shop.shop-checkout-modal.row-value">
						{row.amount !== null && (
							<>
								{(row.key === 'sale' || row.key === 'premium') && '−'}
								{ownRows ? (
									<ShopPrice
										cents={row.amount - row.subtract}
										aznMinor={lira && row.key === 'total' ? chargeMinor(price) * quantity : undefined}
										primaryOnly={row.key !== 'total'}
										payment={payment}
										currency={rowCurrency}
										data-flx="shop.shop-checkout-modal.summary.shop-price--usd"
									/>
								) : (
									<ShopPrice
										aznMinor={row.amount}
										subtractAznMinor={row.subtract}
										primaryOnly={row.key !== 'total'}
										displayQuote={order}
										payment={payment}
										currency={null}
										data-flx="shop.shop-checkout-modal.summary.shop-price"
									/>
								)}
								{hero && row.key === 'total' && period !== null && (
									<span className={styles.period} data-flx="shop.shop-checkout-modal.period">
										{i18n._(PER_PERIOD_DESCRIPTOR, {months: period})}
									</span>
								)}
							</>
						)}
					</dd>
				</div>
			))}
		</dl>
	);
});

// The catalog may be older than a campaign's end; the server's quote below is the price. A renewing plan names its
// month from the server's payable amount and its period total under it.
const PackageOption = observer(({entry, recurring}: {entry: ShopPackageEntry; recurring: boolean}) => {
	const {i18n} = useLingui();
	const quote = recurring ? ShopCatalog.recurringQuotes.get(entry.sku) : undefined;
	const sale = !recurring && campaignShown(entry.price, ShopCatalog.now());
	const cents = sale ? entry.price.sale_usd_cents : entry.price.base_usd_cents;
	const savings = recurring
		? recurringSavingsPercent(entry.months, ShopCatalog.recurringQuotes)
		: planSavingsPercent(entry, ShopCatalog.packageEntries, ShopCatalog.now());
	const total =
		quote &&
		i18n._(RECURRING_TOTAL_DESCRIPTOR, {
			amount: formatRecurringAmount(quote, i18n.locale),
			months: quote.interval_months,
		});
	const monthly = quote?.payable && (
		<ShopPrice
			cents={quote.payable.amount_minor}
			currency={quote.payable.currency}
			months={entry.months}
			primaryOnly
			data-flx="shop.shop-checkout-modal.package-option.monthly"
		/>
	);
	return (
		<div className={styles.package} data-flx="shop.shop-checkout-modal.package">
			<span className={styles.packageName} data-flx="shop.shop-checkout-modal.package-name">
				{i18n._(PACKAGE_MONTHS_DESCRIPTOR, {months: entry.months})}
				{savings !== null && (
					<span className={styles.savings} data-flx="shop.shop-checkout-modal.package-savings">
						{i18n._(SAVE_PERCENT_DESCRIPTOR, {
							percent: getCachedNumberFormat(i18n.locale, {style: 'percent'}).format(savings / 100),
						})}
					</span>
				)}
				{sale && (
					<span className={styles.badge} data-flx="shop.shop-checkout-modal.package-badge">
						{formatDiscount(entry.price.campaign_percent ?? 0, i18n.locale)}
					</span>
				)}
			</span>
			<span className={styles.packagePrice} data-flx="shop.shop-checkout-modal.package-amount">
				{quote ? (
					monthly || total
				) : (
					<ShopPrice
						cents={cents}
						currency={priceCurrency(entry.price)}
						months={entry.months}
						primaryOnly
						data-flx="shop.shop-checkout-modal.package-option.shop-price"
					/>
				)}
			</span>
			{monthly && entry.months > 1 && (
				<span className={styles.packageTotal} data-flx="shop.shop-checkout-modal.package-total">
					{total}
				</span>
			)}
		</div>
	);
});

const OrderNumber = ({orderId}: {orderId: string}) => {
	const {i18n} = useLingui();
	return (
		<div className={styles.order} data-flx="shop.shop-checkout-modal.order">
			<span className={styles.orderText} data-flx="shop.shop-checkout-modal.order-text">
				{i18n._(ORDER_NUMBER_DESCRIPTOR, {orderId})}
			</span>
			<CopyButton
				value={orderId}
				label={COPY_DESCRIPTOR}
				buttonClassName={styles.copyButton}
				iconClassName={styles.copyIcon}
				data-flx="shop.shop-checkout-modal.copy-button"
			/>
		</div>
	);
};

// A username order holds its name while the payment page is open.
const HeldUntil = ({order}: {order: ShopOrderResponse}) => {
	const {i18n} = useLingui();
	if (!order.username) return null;
	const until = new Date(nameHoldEnd(order));
	return (
		<p className={styles.statusBody} data-flx="shop.shop-checkout-modal.held-until">
			{i18n._(HELD_FOR_YOU_UNTIL_DESCRIPTOR, {time: getFormattedTime(until)})}
		</p>
	);
};

const PaymentPriceNote = ({order}: {order: ShopOrderResponse}) => {
	const {i18n} = useLingui();
	if (order.provider === 'paddle') {
		return (
			<p className={styles.note} data-flx="shop.shop-checkout-modal.payment-price-note">
				{i18n._(PADDLE_PRICE_NOTE_DESCRIPTOR, {
					amount: formatPayable(chargeMinor(order), order.currency, i18n.locale),
				})}
			</p>
		);
	}
	const amount = formatAzn(order.amount_azn_minor, i18n.locale);
	const price =
		order.display_currency === 'TRY' && order.display_amount_minor != null
			? formatTry(order.display_amount_minor, i18n.locale)
			: `${formatAzn(order.display_amount_minor ?? order.amount_azn_minor, i18n.locale)} AZN`;
	return (
		<p className={styles.note} data-flx="shop.shop-checkout-modal.payment-price-note">
			{i18n._(PAYMENT_PRICE_NOTE_DESCRIPTOR, {amount, price})}
		</p>
	);
};

interface StatusViewProps {
	icon: React.ReactNode;
	title: string;
	body?: string | null;
	orderId?: string | null;
	children?: React.ReactNode;
}

const StatusView = ({icon, title, body, orderId, children}: StatusViewProps) => (
	<div className={styles.status} data-flx="shop.shop-checkout-modal.status">
		{icon}
		<h2
			ref={focusOnMount}
			tabIndex={-1}
			className={styles.statusTitle}
			data-flx="shop.shop-checkout-modal.status-title"
		>
			{title}
		</h2>
		{body && (
			<p className={styles.statusBody} data-flx="shop.shop-checkout-modal.status-body">
				{body}
			</p>
		)}
		{children}
		{orderId && <OrderNumber orderId={orderId} data-flx="shop.shop-checkout-modal.order-number" />}
	</div>
);

// What a bundle gives, in its order: the catalog's list, and the order's own once one is open.
const BundleContents = ({items}: {items: ReadonlyArray<CosmeticItemResponse>}) => {
	const {i18n} = useLingui();
	return (
		<div className={styles.bundleContents} data-flx="shop.shop-checkout-modal.bundle-contents">
			<h3 className={styles.bundleTitle} data-flx="shop.shop-checkout-modal.bundle-title">
				{i18n._(BUNDLE_INCLUDES_DESCRIPTOR)}
			</h3>
			<ul className={styles.bundleList} data-flx="shop.shop-checkout-modal.bundle-list">
				{items.map((item) => (
					<li key={item.id} className={styles.bundleItem} data-flx="shop.shop-checkout-modal.bundle-item">
						<span className={styles.bundleType} data-flx="shop.shop-checkout-modal.bundle-type">
							{i18n._(COSMETIC_TYPE_LABELS[item.type as CosmeticType])}
						</span>
						<span className={styles.bundleName} data-flx="shop.shop-checkout-modal.bundle-name">
							{cosmeticName(item, i18n.locale)}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
};

// The tooltip on the disabled buy button reaches no touch screen, so the dialog says why too.
const PurchaseBlockNote = observer(() => {
	const {i18n} = useLingui();
	const block = purchaseBlockOf(Users.currentUser);
	if (block === null) return null;
	return (
		<p className={styles.alert} role="status" data-flx="shop.shop-checkout-modal.purchase-block-note">
			{i18n._(purchaseBlockDescriptor(block))}
		</p>
	);
});

// The product's art, then (Plus) its name and price, then what it says about itself.
const Visual = observer(
	({
		target,
		checkout,
		children,
	}: {
		target: ShopCheckoutTarget;
		checkout: ShopCheckoutFlow;
		children: React.ReactNode;
	}) => {
		const {i18n} = useLingui();
		let art: React.ReactNode;
		let after: React.ReactNode = null;
		if (target.kind === 'username') {
			const entry = ShopCatalog.entryForSku(usernameSku(target.length));
			art = (
				<UsernameCardArt
					length={target.length}
					asset={entry?.kind === 'username' ? entry.asset : null}
					name={target.name}
					size="full"
					animated={!Accessibility.useReducedMotion}
					data-flx="shop.shop-checkout-modal.username-card-art"
				/>
			);
			after = (
				<>
					<p className={styles.locked} data-flx="shop.shop-checkout-modal.username-permit">
						{i18n._(USERNAME_CARD_PERMIT_DESCRIPTOR)}
					</p>
					{target.offerEndsAt && (
						<p className={styles.locked} data-flx="shop.shop-checkout-modal.username-offer-until">
							{i18n._(USERNAME_OFFER_UNTIL_DESCRIPTOR, {date: getFormattedFullDate(new Date(target.offerEndsAt))})}
						</p>
					)}
				</>
			);
		} else if (target.kind === 'bundle') {
			art = checkout.bundle && (
				<div className={styles.stage} data-flx="shop.shop-checkout-modal.bundle-stage-box">
					<ShopItemStage items={checkout.bundle.items} size="grid" data-flx="shop.shop-checkout-modal.bundle-stage" />
				</div>
			);
		} else {
			const stageItem = target.kind === 'premium' ? (target.fromItem ?? null) : checkout.item;
			art = stageItem ? (
				<div className={styles.stage} data-flx="shop.shop-checkout-modal.stage-box">
					<ShopItemStage items={[stageItem]} size="grid" data-flx="shop.shop-checkout-modal.shop-item-stage" />
				</div>
			) : (
				<div className={styles.crownBox} data-flx="shop.shop-checkout-modal.crown-box">
					{target.kind === 'boost' ? (
						<span className={styles.gem} data-flx="shop.shop-checkout-modal.gem">
							<BoostGemIcon />
						</span>
					) : (
						<CrownIcon weight="fill" className={styles.crown} data-flx="shop.shop-checkout-modal.crown" />
					)}
				</div>
			);
			if (stageItem && target.kind === 'premium') {
				after = (
					<p className={styles.locked} data-flx="shop.shop-checkout-modal.locked">
						{i18n._(LOCKED_DESCRIPTOR, {
							name: cosmeticName(stageItem, i18n.locale),
							premiumProductName: PREMIUM_PRODUCT_NAME,
						})}
					</p>
				);
			}
		}
		return (
			<div className={styles.visual} data-flx="shop.shop-checkout-modal.visual">
				{art}
				{children}
				{after}
			</div>
		);
	},
);

const SummaryView = observer(
	({
		target,
		checkout,
		months,
		onMonths,
		nameStep,
	}: {
		target: ShopCheckoutTarget;
		checkout: ShopCheckoutFlow;
		months: 1 | 3 | 12;
		onMonths: (months: 1 | 3 | 12) => void;
		// A username card's name field, above the price.
		nameStep?: React.ReactNode;
	}) => {
		const {i18n} = useLingui();
		const quote = checkout.quote;
		const isPremiumMember = Users.currentUser?.isPremium() ?? false;
		const recurring = checkout.billingMode === 'recurring';
		const terms = checkout.recurringTerms;
		const renewal = terms && {
			amount: formatPayable(terms.amount_minor, terms.currency, i18n.locale),
			months: terms.interval_months,
			premiumProductName: PREMIUM_PRODUCT_NAME,
		};
		const subscribed = target.kind === 'premium' && holdsPlusSubscription(Users.currentUser?.id);
		const itemPrice =
			(target.kind === 'item' || target.kind === 'bundle') && quote?.premium_percent === 0
				? (ShopCatalog.entryForSku(target.sku)?.price ?? null)
				: null;
		const itemPremiumPrice = itemPrice?.premium_usd_cents ?? null;
		// How this quote is charged; before it comes, the catalog's route for this viewer (null: nobody sells here).
		const route: ShopViewerPayment | null | undefined =
			quote && ShopCatalog.payment !== null ? chargePayment(quote, ShopCatalog.payment) : ShopCatalog.payment;
		const oneTime = (target.kind === 'premium' && !recurring && !subscribed) || target.kind === 'boost';
		const name =
			target.kind === 'premium'
				? `${PREMIUM_PRODUCT_NAME} · ${i18n._(PACKAGE_MONTHS_DESCRIPTOR, {months})}`
				: target.kind === 'visionary'
					? i18n._(VISIONARY_NAME_DESCRIPTOR)
					: target.kind === 'boost'
						? i18n._(BOOST_SLOT_NAME_DESCRIPTOR)
						: target.kind === 'item'
							? checkout.item && cosmeticName(checkout.item, i18n.locale)
							: target.kind === 'bundle'
								? checkout.bundle && cosmeticName(checkout.bundle, i18n.locale)
								: null;
		// One-time boosts through Payriff: how many 30 day slots, at the quote's price of one, in one payment; at most what
		// max_purchased_boost_slots leaves beside the live bought slots (the order counts its open orders too) and the
		// request schema's 100.
		const boostMax =
			target.kind === 'boost'
				? Math.max(
						1,
						Math.min(
							PADDLE_BOOST_MAX_QUANTITY,
							LimitResolver.resolve({
								key: 'max_purchased_boost_slots',
								fallback: DEFAULT_FREE_LIMITS.max_purchased_boost_slots,
							}) - liveBoughtSlotCount(GuildBoosts.slots, Date.now()),
						),
					)
				: 1;
		const payriffBoost = target.kind === 'boost' && quote !== null && route?.provider === 'payriff';
		useEffect(() => {
			if (payriffBoost && checkout.boostCount > boostMax) checkout.setBoostCount(boostMax);
		}, [payriffBoost, boostMax, checkout, checkout.boostCount]);
		const boosts = payriffBoost ? Math.min(checkout.boostCount, boostMax) : 1;
		const liraUnit = quote?.list_payable?.try_minor;
		const boostStep = payriffBoost && quote && (
			<BoostQuantityStep
				oneTime
				quantity={boosts}
				max={boostMax}
				unitMinor={liraUnit != null ? liraPayable(liraUnit, quote) : chargeMinor(quote)}
				currency={liraUnit != null ? 'TRY' : 'AZN'}
				format={(minor) => (liraUnit != null ? formatTry(minor, i18n.locale) : `${formatAzn(minor, i18n.locale)} AZN`)}
				onChange={checkout.setBoostCount}
				disabled={checkout.state === 'creating'}
			/>
		);
		const offer = (
			<div className={styles.offer} data-flx="shop.shop-checkout-modal.offer">
				{name && (
					<p className={styles.offerName} data-flx="shop.shop-checkout-modal.offer-name">
						{name}
					</p>
				)}
				{quote ? (
					<Summary
						price={quote}
						hero
						period={recurring ? months : null}
						quantity={boosts}
						data-flx="shop.shop-checkout-modal.summary-part"
					/>
				) : (
					<div
						className={styles.skeleton}
						role="status"
						aria-busy="true"
						aria-label={i18n._(LOADING_PRICE_DESCRIPTOR)}
						data-flx="shop.shop-checkout-modal.skeleton"
					>
						<span className={styles.skeletonTotal} data-flx="shop.shop-checkout-modal.skeleton-total" />
						<span className={styles.skeletonLine} data-flx="shop.shop-checkout-modal.skeleton-line" />
					</div>
				)}
				{route?.provider === 'paddle' && (
					<p className={styles.taxes} data-flx="shop.shop-checkout-modal.taxes">
						{i18n._(TAXES_INCLUDED_DESCRIPTOR)}
					</p>
				)}
			</div>
		);
		const plans = target.kind === 'premium';
		// A typed name is priced by its own check in the name step; only a named username shows its quote here.
		const showOffer = target.kind !== 'username' || target.name !== undefined;
		return (
			<div className={styles.layout} data-flx="shop.shop-checkout-modal.layout">
				<div className={styles.grid} data-flx="shop.shop-checkout-modal.grid">
					<Visual target={target} checkout={checkout} data-flx="shop.shop-checkout-modal.visual-part">
						{plans && offer}
					</Visual>
					<div className={styles.details} data-flx="shop.shop-checkout-modal.details">
						{nameStep}
						{!plans && showOffer && boostStep}
						{!plans && showOffer && offer}
						{target.kind === 'bundle' && checkout.bundle && (
							<BundleContents items={checkout.bundle.items} data-flx="shop.shop-checkout-modal.bundle-contents-part" />
						)}
						{target.kind === 'premium' && (
							<RadioGroup
								className={styles.plans}
								aria-label={i18n._(CHOOSE_A_PLAN_DESCRIPTOR)}
								options={ShopCatalog.packageEntries.map((entry) => ({
									value: entry.months,
									name: i18n._(PACKAGE_MONTHS_DESCRIPTOR, {months: entry.months}),
								}))}
								value={months}
								disabled={checkout.state === 'creating'}
								onChange={onMonths}
								renderContent={(option) => {
									const entry = ShopCatalog.packageEntries.find((candidate) => candidate.months === option.value);
									return entry ? (
										<PackageOption
											entry={entry}
											recurring={recurring}
											data-flx="shop.shop-checkout-modal.package-option"
										/>
									) : null;
								}}
								data-flx="shop.shop-checkout-modal.radio-group"
							/>
						)}
						{target.kind === 'visionary' && (
							<p className={styles.rewards} data-flx="shop.shop-checkout-modal.rewards">
								{i18n._(VISIONARY_REWARDS_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
							</p>
						)}
						{target.kind === 'boost' && !boostStep && (
							<p className={styles.rewards} data-flx="shop.shop-checkout-modal.boost-note">
								{i18n._(BOOST_SLOT_NOTE_DESCRIPTOR)}
							</p>
						)}
						<PurchaseBlockNote />
						{subscribed && (
							<p className={styles.alert} role="status" data-flx="shop.shop-checkout-modal.subscribed-note">
								{i18n._(SUBSCRIPTION_BLOCKS_ONE_TIME_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
							</p>
						)}
						{checkout.state === 'recurring_unavailable' && (
							<p className={styles.alert} role="alert" data-flx="shop.shop-checkout-modal.recurring-unavailable">
								{i18n._(RECURRING_UNAVAILABLE_DESCRIPTOR)}
							</p>
						)}
						{renewal && (
							<Checkbox
								checked={checkout.recurringConsent}
								disabled={checkout.state === 'creating'}
								className={styles.consentRow}
								onChange={checkout.setRecurringConsent}
								data-flx="shop.shop-checkout-modal.checkbox.recurring-consent"
							>
								<span className={styles.consent} data-flx="shop.shop-checkout-modal.recurring-consent">
									{i18n._(RECURRING_CONSENT_DESCRIPTOR, renewal)}
								</span>
							</Checkbox>
						)}
						{checkout.recurringQuoteChanged && (
							<p className={styles.alert} role="alert" data-flx="shop.shop-checkout-modal.recurring-changed">
								{i18n._(RECURRING_QUOTE_CHANGED_DESCRIPTOR)}
							</p>
						)}
						{itemPremiumPrice != null && (
							<p className={styles.premiumHint} data-flx="shop.shop-checkout-modal.premium-hint">
								<CrownIcon weight="fill" className={styles.hintCrown} data-flx="shop.shop-checkout-modal.hint-crown" />
								<span data-flx="shop.shop-checkout-modal.premium-hint-text">
									<ShopPrice
										cents={itemPremiumPrice}
										currency={itemPrice && priceCurrency(itemPrice)}
										label={PREMIUM_PRICE_DESCRIPTOR}
										primaryOnly
										data-flx="shop.shop-checkout-modal.summary-view.shop-price"
									/>
								</span>
							</p>
						)}
						<div className={styles.notes} data-flx="shop.shop-checkout-modal.notes">
							{renewal && (
								<p className={styles.note} data-flx="shop.shop-checkout-modal.renewal-note">
									{i18n._(RECURRING_RENEWAL_NOTE_DESCRIPTOR, renewal)}
								</p>
							)}
							{oneTime && (
								<p className={styles.note} data-flx="shop.shop-checkout-modal.one-time">
									<span data-flx="shop.shop-checkout-modal.one-time-note">
										{i18n._(boostStep ? BOOST_ONE_TIME_NOTE_DESCRIPTOR : ONE_TIME_PAYMENT_DESCRIPTOR)}
									</span>
									{target.kind === 'premium' && isPremiumMember && (
										<>
											{' '}
											<span data-flx="shop.shop-checkout-modal.time-added-note">
												{i18n._(TIME_ADDED_ON_TOP_DESCRIPTOR)}
											</span>
										</>
									)}
								</p>
							)}
							{target.kind === 'premium' && !recurring && !subscribed && (
								<ShopRecurringReason className={styles.note} data-flx="shop.shop-checkout-modal.recurring-reason" />
							)}
							{target.kind === 'visionary' && (
								<p className={styles.note} data-flx="shop.shop-checkout-modal.lifetime-note">
									{i18n._(LIFETIME_MEANS_DESCRIPTOR)}
								</p>
							)}
							{target.kind === 'visionary' && isPremiumMember && (
								<p className={styles.note} data-flx="shop.shop-checkout-modal.kept-time-note">
									{i18n._(REMAINING_TIME_KEPT_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
								</p>
							)}
							{!(target.kind === 'username' && target.visionary) && (
								<p className={styles.note} data-flx="shop.shop-checkout-modal.azn-note">
									{route?.provider === 'paddle' ? (
										i18n._(CHECKOUT_PADDLE_NOTE_DESCRIPTOR)
									) : route?.provider === 'payriff' && route.currency === 'AZN' ? (
										i18n._(CHECKOUT_PAYRIFF_AZN_NOTE_DESCRIPTOR)
									) : (
										<ShopCurrencyNote
											payment={route}
											data-flx="shop.shop-checkout-modal.summary-view.shop-currency-note"
										/>
									)}
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	},
);

// A monthly boost subscription: how many boosts at the catalog's price of one, and the consent for the monthly total.
const BoostSubscriptionView = observer(
	({
		checkout,
		quantity,
		maxQuantity,
		onQuantity,
	}: {
		checkout: ShopCheckoutFlow;
		quantity: number;
		maxQuantity: number;
		onQuantity: (quantity: number) => void;
	}) => {
		const {i18n} = useLingui();
		const offer = ShopCatalog.boost?.recurring_quote;
		const terms = checkout.recurringTerms;
		const unavailable = (
			<p className={styles.alert} role="alert" data-flx="shop.shop-checkout-modal.boost-unavailable">
				{i18n._(BOOST_SUBSCRIPTION_UNAVAILABLE_DESCRIPTOR)}
			</p>
		);
		if (!offer) return unavailable;
		const amount = formatPayable(terms?.amount_minor ?? offer.amount_minor * quantity, offer.currency, i18n.locale);
		return (
			<BoostQuantityStep
				quantity={quantity}
				max={Math.max(1, maxQuantity)}
				unitMinor={offer.amount_minor}
				currency={offer.currency}
				onChange={onQuantity}
				disabled={checkout.state === 'creating' || maxQuantity < 1}
				steps={[i18n._(CHOOSE_QUANTITY_STEP_DESCRIPTOR), i18n._(PAYMENT_STEP_DESCRIPTOR)]}
			>
				{maxQuantity < 1 && (
					<p className={styles.alert} role="alert" data-flx="shop.shop-checkout-modal.boost-full">
						{i18n._(BOOST_LIMIT_TITLE_DESCRIPTOR)}
					</p>
				)}
				<PurchaseBlockNote />
				{checkout.state === 'recurring_unavailable' && unavailable}
				{terms && (
					<Checkbox
						checked={checkout.recurringConsent}
						disabled={checkout.state === 'creating'}
						className={styles.consentRow}
						onChange={checkout.setRecurringConsent}
						data-flx="shop.shop-checkout-modal.checkbox.boost-consent"
					>
						<span className={styles.consent} data-flx="shop.shop-checkout-modal.boost-consent">
							{i18n._(BOOST_RECURRING_CONSENT_DESCRIPTOR, {amount})}
						</span>
					</Checkbox>
				)}
				{checkout.recurringQuoteChanged && (
					<p className={styles.alert} role="alert" data-flx="shop.shop-checkout-modal.boost-changed">
						{i18n._(RECURRING_QUOTE_CHANGED_DESCRIPTOR)}
					</p>
				)}
				<div className={styles.notes} data-flx="shop.shop-checkout-modal.boost-notes">
					<p className={styles.note} data-flx="shop.shop-checkout-modal.boost-renewal-note">
						{i18n._(BOOST_RENEWAL_NOTE_DESCRIPTOR, {amount})}
					</p>
					<p className={styles.note} data-flx="shop.shop-checkout-modal.boost-paddle-note">
						{i18n._(TAXES_INCLUDED_DESCRIPTOR)} · {i18n._(CHECKOUT_PADDLE_NOTE_DESCRIPTOR)}
					</p>
				</div>
			</BoostQuantityStep>
		);
	},
);

// Web: a real link, which no pop-up blocker stops. Desktop: the system browser,
// as Fluxer opens Stripe; no deep link back.
const PaymentPageButton = ({checkout, label}: {checkout: ShopCheckoutFlow; label: string}) => {
	const url = checkout.order?.payment_url;
	// The server only hands out https Payriff pages; anything else never becomes a link.
	if (!url?.startsWith('https://')) return null;
	if (isDesktop()) {
		return (
			<Button onClick={() => checkout.openPaymentPage()} data-flx="shop.shop-checkout-modal.button.open-payment-page">
				{label}
			</Button>
		);
	}
	return (
		<a
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			className={styles.paymentLink}
			data-flx="shop.shop-checkout-modal.payment-link"
		>
			{label}
		</a>
	);
};

export const ShopCheckoutModal = observer<ShopCheckoutModalProps>(({target}) => {
	const {i18n} = useLingui();
	const [months, setMonths] = useState<1 | 3 | 12>(target.kind === 'premium' ? target.months : 1);
	const [boosting, setBoosting] = useState(false);
	const recurringBoost = target.kind === 'boost' ? target.recurring : undefined;
	const [quantity, setQuantity] = useState(recurringBoost?.quantity ?? 1);
	// Plus renews for an account the catalog offers renewal to, and is bought once otherwise.
	const checkout = useShopCheckout(
		target.kind === 'premium'
			? {...target, months, ...(ShopCatalog.plusRenews && {billingMode: 'recurring' as const})}
			: target.kind === 'boost' && recurringBoost
				? {...target, recurring: {...recurringBoost, quantity}}
				: target,
	);
	// A username card: the typed name and its check, kept while the confirm step is shown.
	const fixedName = target.kind === 'username' ? target.name : undefined;
	const freeOnly = target.kind === 'username' && target.visionary === true;
	const [cardName, setCardName] = useState(fixedName ?? '');
	const offer = target.kind === 'username' ? target.offer : undefined;
	const checked = useUsernameCardAvailability(
		cardName,
		target.kind === 'username' && offer === undefined ? target.length : null,
		checkout.nameChecks,
	);
	// The check answers an offer's name taken to everyone, its buyer too; the offer's quote checks the name and the
	// code instead, and the order checks them again.
	const availability: UsernameCardAvailability =
		offer === undefined
			? checked
			: {
					state: checkout.nameRefusal ?? (checkout.quote ? 'available' : 'checking'),
					username: checked.username,
					price: null,
					visionaryFree: false,
				};
	const nameReady = availability.state === 'available' || availability.state === 'held_by_you';
	// The confirm step's quote prices the name; a check that names no price only for a name the Visionary right gives
	// (an older API names none at all) keeps the purchase closed.
	const namePurchasable = nameReady && (availability.price !== null || !availability.visionaryFree);
	const takeFree = () => checkout.confirmFree(availability.username);
	// A typed name is quoted only once it is confirmed: the purchase is busy until the confirm step shows.
	const confirmingName = target.kind === 'username' && fixedName === undefined && checkout.state === 'quoting';
	// A renewing plan that starts with a free trial says so on its button.
	const trialDays =
		(checkout.billingMode === 'recurring' &&
			(
				ShopCatalog.recurringQuotes.get(`premium:${months}`) as
					| (RecurringQuote & {trial_days?: number | null})
					| undefined
			)?.trial_days) ||
		null;
	const reducedMotion = Accessibility.useReducedMotion;
	// A free name asks for the same account as a bought one.
	const purchaseBlock = purchaseBlockOf(Users.currentUser);
	const purchaseBlockText = i18n._(purchaseBlockDescriptor(purchaseBlock));
	const decorations = target.kind === 'item' || target.kind === 'bundle';
	const order = checkout.order;
	const nameHoldOver = useNameHoldOver(order);
	const close = () => ModalCommands.pop();
	// After success, close either preview without waiting for the owned-items cache to load.
	const closeAll = () => {
		ModalCommands.pop();
		ModalCommands.popByType(ShopItemDetailModal);
		ModalCommands.popByType(ShopBundleDetailModal);
	};
	const product = target.kind === 'item' ? checkout.item : checkout.bundle;
	const title = decorations
		? i18n._(BUY_ITEM_DESCRIPTOR, {name: product ? cosmeticName(product, i18n.locale) : ''})
		: target.kind === 'premium'
			? i18n._(BUY_PREMIUM_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})
			: recurringBoost
				? i18n._(BOOST_SUBSCRIPTION_TITLE_DESCRIPTOR)
				: target.kind === 'boost'
					? i18n._(BUY_BOOST_DESCRIPTOR)
					: target.kind === 'username'
						? fixedName !== undefined
							? `@${fixedName}`
							: freeOnly
								? i18n._(VISIONARY_NAME_RIGHT_TITLE_DESCRIPTOR)
								: i18n._(USERNAME_CARD_TITLE_DESCRIPTOR, {length: target.length})
						: i18n._(VISIONARY_NAME_DESCRIPTOR);
	const closeButton = (
		<Button variant="secondary" onClick={close} data-flx="shop.shop-checkout-modal.button.close">
			{i18n._(CLOSE_DESCRIPTOR)}
		</Button>
	);
	const dangerIcon = (
		<XCircleIcon
			weight="fill"
			className={clsx(styles.statusIcon, styles.danger)}
			data-flx="shop.shop-checkout-modal.x-circle-icon"
		/>
	);
	const warningIcon = (
		<WarningIcon weight="fill" className={styles.statusIcon} data-flx="shop.shop-checkout-modal.warning-icon" />
	);
	// A username order opens from the confirm step, which stays on screen while it does.
	const state = target.kind === 'username' && checkout.state === 'creating' ? 'name_confirm' : checkout.state;
	let view: string = state;
	let body: React.ReactNode;
	let footer: React.ReactNode;
	switch (state) {
		case 'quoting':
		case 'ready':
		case 'creating':
		case 'recurring_unavailable':
			view = 'summary';
			body = recurringBoost ? (
				<BoostSubscriptionView
					checkout={checkout}
					quantity={quantity}
					maxQuantity={recurringBoost.maxQuantity}
					onQuantity={setQuantity}
				/>
			) : (
				<SummaryView
					target={target}
					checkout={checkout}
					months={months}
					onMonths={setMonths}
					nameStep={
						target.kind === 'username' && (
							<UsernameCardNameStep
								length={target.length}
								value={cardName}
								onChange={setCardName}
								availability={availability}
								refusal={checkout.username === availability.username ? checkout.nameRefusal : null}
								fixed={fixedName !== undefined}
								showPrice={fixedName === undefined && !freeOnly}
								onTakeFree={freeOnly || purchaseBlock != null ? undefined : takeFree}
								data-flx="shop.shop-checkout-modal.username-card-name-step"
							/>
						)
					}
					data-flx="shop.shop-checkout-modal.summary-view"
				/>
			);
			footer = (
				<>
					<Button variant="secondary" onClick={close} data-flx="shop.shop-checkout-modal.button.cancel">
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
					{freeOnly ? (
						<PurchaseDisabledWrapper
							disabled={purchaseBlock != null}
							tooltipText={purchaseBlockText}
							data-flx="shop.shop-checkout-modal.take-free-disabled-wrapper"
						>
							<Button
								onClick={takeFree}
								disabled={!nameReady || !availability.visionaryFree || purchaseBlock != null}
								data-flx="shop.shop-checkout-modal.button.take-free"
							>
								{i18n._(TAKE_IT_FREE_DESCRIPTOR)}
							</Button>
						</PurchaseDisabledWrapper>
					) : (
						<PurchaseDisabledWrapper
							disabled={purchaseBlock != null}
							tooltipText={purchaseBlockText}
							data-flx="shop.shop-checkout-modal.purchase-disabled-wrapper"
						>
							<Button
								onClick={() =>
									void (target.kind === 'username' ? checkout.confirmName(availability.username) : checkout.start())
								}
								submitting={checkout.state === 'creating' || confirmingName}
								disabled={
									checkout.state === 'quoting' ||
									checkout.state === 'recurring_unavailable' ||
									purchaseBlock != null ||
									ShopCatalog.payment === null ||
									(target.kind === 'username' && !namePurchasable) ||
									(checkout.billingMode === 'recurring' && !checkout.recurringConsent) ||
									(target.kind === 'premium' && holdsPlusSubscription(Users.currentUser?.id)) ||
									(recurringBoost !== undefined && recurringBoost.maxQuantity < 1)
								}
								data-flx="shop.shop-checkout-modal.button.purchase"
							>
								{recurringBoost
									? i18n._(CONTINUE_DESCRIPTOR)
									: trialDays
										? i18n._(TRY_FREE_FOR_DAYS_DESCRIPTOR, {days: trialDays})
										: i18n._(checkout.billingMode === 'recurring' ? SUBSCRIBE_DESCRIPTOR : PURCHASE_DESCRIPTOR)}
							</Button>
						</PurchaseDisabledWrapper>
					)}
				</>
			);
			break;
		case 'name_confirm':
			if (checkout.free) {
				body = (
					<StatusView
						icon={<AtIcon className={styles.statusIcon} data-flx="shop.shop-checkout-modal.free-confirm-icon" />}
						title={i18n._(TAKE_NAME_FREE_TITLE_DESCRIPTOR, {name: checkout.username ?? ''})}
						body={i18n._(VISIONARY_RIGHT_ONCE_DESCRIPTOR)}
						data-flx="shop.shop-checkout-modal.free-confirm"
					>
						<p className={styles.statusBody} data-flx="shop.shop-checkout-modal.free-short-name-released">
							{i18n._(SHORT_NAME_RELEASED_LATER_DESCRIPTOR)}
						</p>
					</StatusView>
				);
				footer = (
					<>
						<Button
							variant="secondary"
							onClick={() => checkout.changeName()}
							disabled={checkout.state === 'creating'}
							data-flx="shop.shop-checkout-modal.button.free-change-name"
						>
							{i18n._(CHANGE_NAME_DESCRIPTOR)}
						</Button>
						<Button
							onClick={() => void checkout.claimFree()}
							submitting={checkout.state === 'creating'}
							disabled={purchaseBlock != null}
							data-flx="shop.shop-checkout-modal.button.claim-free"
						>
							{i18n._(TAKE_IT_FREE_DESCRIPTOR)}
						</Button>
					</>
				);
				break;
			}
			body = (
				<StatusView
					icon={<AtIcon className={styles.statusIcon} data-flx="shop.shop-checkout-modal.name-confirm-icon" />}
					title={i18n._(HOLD_NAME_FOR_YOU_DESCRIPTOR, {name: checkout.username ?? ''})}
					body={i18n._(PAY_WITHIN_5_MINUTES_DESCRIPTOR)}
					data-flx="shop.shop-checkout-modal.name-confirm"
				>
					{fixedName === undefined && namePriceMoved(availability.price, checkout.quote) && (
						<p className={styles.alert} role="alert" data-flx="shop.shop-checkout-modal.name-price-changed">
							{i18n._(PRICE_CHANGED_DESCRIPTOR)}
						</p>
					)}
					{checkout.quote && (
						<Summary price={checkout.quote} hero data-flx="shop.shop-checkout-modal.name-confirm-summary" />
					)}
					<p className={styles.statusBody} data-flx="shop.shop-checkout-modal.name-becomes-yours">
						{i18n._(NAME_BECOMES_YOURS_DESCRIPTOR)}
					</p>
					<p className={styles.statusBody} data-flx="shop.shop-checkout-modal.short-name-released">
						{i18n._(SHORT_NAME_RELEASED_LATER_DESCRIPTOR)}
					</p>
				</StatusView>
			);
			footer = (
				<>
					<Button
						variant="secondary"
						onClick={() => checkout.changeName()}
						disabled={checkout.state === 'creating'}
						data-flx="shop.shop-checkout-modal.button.change-name"
					>
						{i18n._(CHANGE_NAME_DESCRIPTOR)}
					</Button>
					<Button
						onClick={() => void checkout.start()}
						submitting={checkout.state === 'creating'}
						data-flx="shop.shop-checkout-modal.button.hold-and-pay"
					>
						{i18n._(HOLD_AND_PAY_DESCRIPTOR)}
					</Button>
				</>
			);
			break;
		case 'payment_ready':
			body = order && (
				<StatusView
					icon={<ArrowSquareOutIcon className={styles.statusIcon} data-flx="shop.shop-checkout-modal.ready-icon" />}
					title={i18n._(OPEN_PAYMENT_PAGE_DESCRIPTOR)}
					orderId={order.id}
					data-flx="shop.shop-checkout-modal.payment-ready"
				>
					<HeldUntil order={order} data-flx="shop.shop-checkout-modal.payment-ready-held-until" />
					<PaymentPriceNote order={order} />
				</StatusView>
			);
			footer = (
				<>
					<Button variant="secondary" onClick={close} data-flx="shop.shop-checkout-modal.button.ready-cancel">
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
					<Button
						onClick={() => checkout.openPaymentPage()}
						disabled={nameHoldOver}
						data-flx="shop.shop-checkout-modal.button.ready-continue"
					>
						{i18n._(CONTINUE_DESCRIPTOR)}
					</Button>
				</>
			);
			break;
		case 'price_changed':
		case 'contents_changed': {
			const contentsChanged = checkout.state === 'contents_changed';
			body = (
				<div className={styles.priceChanged} data-flx="shop.shop-checkout-modal.price-changed">
					<h2
						ref={focusOnMount}
						tabIndex={-1}
						className={styles.statusTitle}
						data-flx="shop.shop-checkout-modal.price-changed-title"
					>
						{i18n._(contentsChanged ? BUNDLE_CONTENTS_CHANGED_DESCRIPTOR : PRICE_CHANGED_DESCRIPTOR)}
					</h2>
					{contentsChanged && checkout.bundle && (
						<BundleContents items={checkout.bundle.items} data-flx="shop.shop-checkout-modal.changed-contents" />
					)}
					{order && checkout.quote && (
						<Summary
							price={order}
							before={checkout.quote}
							order={order}
							data-flx="shop.shop-checkout-modal.new-summary"
						/>
					)}
					{order && <PaymentPriceNote order={order} />}
				</div>
			);
			footer = (
				<>
					<Button variant="secondary" onClick={close} data-flx="shop.shop-checkout-modal.button.cancel--2">
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
					<Button
						onClick={() => checkout.openPaymentPage()}
						disabled={nameHoldOver}
						data-flx="shop.shop-checkout-modal.button.continue"
					>
						{i18n._(contentsChanged ? CONTINUE_DESCRIPTOR : CONTINUE_AT_NEW_PRICE_DESCRIPTOR)}
					</Button>
				</>
			);
			break;
		}
		case 'awaiting': {
			// A free Visionary name has no payment: only its delivery is waited for.
			if (checkout.free) {
				body = (
					<StatusView
						icon={<AtIcon className={styles.statusIcon} data-flx="shop.shop-checkout-modal.free-awaiting-icon" />}
						title={i18n._(GIVING_YOU_NAME_DESCRIPTOR, {name: checkout.username ?? ''})}
						orderId={order?.id}
						data-flx="shop.shop-checkout-modal.free-awaiting"
					>
						<div className={styles.waiting} role="status" data-flx="shop.shop-checkout-modal.free-waiting">
							<Spinner size="small" data-flx="shop.shop-checkout-modal.free-spinner" />
							<span data-flx="shop.shop-checkout-modal.free-waiting-text">
								{i18n._(SETTING_UP_USERNAME_DESCRIPTOR)}
							</span>
						</div>
					</StatusView>
				);
				footer = closeButton;
				break;
			}
			const paymentProviderName = order?.provider === 'paddle' ? PADDLE_NAME : PAYMENT_PROVIDER_NAME;
			body = (
				<StatusView
					icon={<ArrowSquareOutIcon className={styles.statusIcon} data-flx="shop.shop-checkout-modal.arrow-icon" />}
					title={i18n._(checkout.inPage ? FINISH_PAYING_IN_WINDOW_DESCRIPTOR : FINISH_PAYING_IN_BROWSER_DESCRIPTOR, {
						paymentProviderName,
					})}
					body={i18n._(checkout.inPage ? PAYMENT_WINDOW_OPEN_DESCRIPTOR : PAYMENT_PAGE_OPENED_DESCRIPTOR, {
						paymentProviderName,
					})}
					orderId={order?.id}
					data-flx="shop.shop-checkout-modal.awaiting"
				>
					{order && <HeldUntil order={order} data-flx="shop.shop-checkout-modal.awaiting-held-until" />}
					{order && <PaymentPriceNote order={order} />}
					{order && order.provider !== 'paddle' && (
						<p className={styles.statusBody} data-flx="shop.shop-checkout-modal.payment-language">
							{i18n._(PAYMENT_PAGE_LANGUAGE_DESCRIPTOR, {
								paymentProviderName: PAYMENT_PROVIDER_NAME,
								amount: `${formatAzn(order.amount_azn_minor, i18n.locale)} AZN`,
							})}
						</p>
					)}
					<div className={styles.waiting} role="status" data-flx="shop.shop-checkout-modal.waiting">
						<Spinner size="small" data-flx="shop.shop-checkout-modal.spinner" />
						<span data-flx="shop.shop-checkout-modal.waiting-text">{i18n._(WAITING_FOR_PAYMENT_DESCRIPTOR)}</span>
					</div>
				</StatusView>
			);
			footer = (
				<>
					{closeButton}
					{!checkout.inPage && !nameHoldOver && (
						<PaymentPageButton
							checkout={checkout}
							label={i18n._(OPEN_PAYMENT_PAGE_DESCRIPTOR)}
							data-flx="shop.shop-checkout-modal.payment-page-button"
						/>
					)}
				</>
			);
			break;
		}
		case 'approved': {
			body = checkout.success && (
				<ShopEquippedSuccess result={checkout.success} data-flx="shop.shop-checkout-modal.shop-equipped-success" />
			);
			const here = target.kind === 'boost' ? target.here : undefined;
			if (here) {
				const boostHere = async () => {
					setBoosting(true);
					const boosted = await here.boost();
					setBoosting(false);
					if (boosted) closeAll();
				};
				footer = (
					<>
						<Button variant="secondary" onClick={closeAll} data-flx="shop.shop-checkout-modal.button.boost-later">
							{i18n._(OKAY_DESCRIPTOR)}
						</Button>
						<Button
							onClick={() => void boostHere()}
							submitting={boosting}
							disabled={!here.ready()}
							data-flx="shop.shop-checkout-modal.button.boost-here"
						>
							{i18n._(BOOST_THIS_COMMUNITY_DESCRIPTOR)}
						</Button>
					</>
				);
				break;
			}
			footer = (
				<>
					{decorations && (
						<Button
							variant="secondary"
							onClick={() => {
								closeAll();
								ModalCommands.push(
									modal(() => (
										<UserSettingsModal
											initialTab="profile_cosmetics"
											data-flx="shop.shop-checkout-modal.user-settings-modal"
										/>
									)),
								);
							}}
							data-flx="shop.shop-checkout-modal.button.profile-decorations"
						>
							{i18n._(PROFILE_DECORATIONS_DESCRIPTOR)}
						</Button>
					)}
					<Button onClick={closeAll} data-flx="shop.shop-checkout-modal.button.okay">
						{i18n._(OKAY_DESCRIPTOR)}
					</Button>
				</>
			);
			break;
		}
		case 'boost_limit':
			body = (
				<StatusView
					icon={warningIcon}
					title={i18n._(BOOST_LIMIT_TITLE_DESCRIPTOR)}
					body={checkout.boostLimit === null ? null : i18n._(BOOST_LIMIT_DESCRIPTOR, {count: checkout.boostLimit})}
					data-flx="shop.shop-checkout-modal.boost-limit"
				/>
			);
			footer = closeButton;
			break;
		case 'failed':
		case 'failed_mismatch':
			body = (
				<StatusView
					icon={dangerIcon}
					title={i18n._(PAYMENT_NOT_COMPLETED_DESCRIPTOR)}
					body={i18n._(checkout.state === 'failed' ? PAYMENT_NOT_APPROVED_DESCRIPTOR : ORDER_MISMATCH_DESCRIPTOR)}
					orderId={order?.id}
					data-flx="shop.shop-checkout-modal.failed"
				/>
			);
			footer = (
				<>
					{closeButton}
					{checkout.state === 'failed' && (
						<Button onClick={() => checkout.retry()} data-flx="shop.shop-checkout-modal.button.retry">
							{i18n._(TRY_AGAIN_DESCRIPTOR)}
						</Button>
					)}
				</>
			);
			break;
		case 'timeout':
			body = (
				<StatusView
					icon={warningIcon}
					title={i18n._(PAYMENT_NOT_CONFIRMED_YET_DESCRIPTOR)}
					body={i18n._(PAYMENT_TIMEOUT_DESCRIPTOR)}
					orderId={order?.id}
					data-flx="shop.shop-checkout-modal.timeout"
				/>
			);
			footer = (
				<>
					{closeButton}
					<Button
						onClick={() => void checkout.checkAgain()}
						submitting={checkout.checking}
						data-flx="shop.shop-checkout-modal.button.check-again"
					>
						{i18n._(CHECK_AGAIN_DESCRIPTOR)}
					</Button>
				</>
			);
			break;
		case 'sales_closed':
		case 'sold_out':
			if (checkout.free) {
				body = (
					<StatusView
						icon={warningIcon}
						title={i18n._(VISIONARY_RIGHT_UNAVAILABLE_DESCRIPTOR)}
						data-flx="shop.shop-checkout-modal.free-closed"
					/>
				);
				footer = closeButton;
				break;
			}
			body = (
				<StatusView
					icon={<StorefrontIcon className={styles.statusIcon} data-flx="shop.shop-checkout-modal.storefront-icon" />}
					title={i18n._(checkout.state === 'sold_out' ? SOLD_OUT_DESCRIPTOR : SALES_PAUSED_DESCRIPTOR)}
					body={i18n._(
						checkout.state === 'sold_out' ? VISIONARY_SOLD_OUT_DESCRIPTOR : CANT_BE_BOUGHT_RIGHT_NOW_DESCRIPTOR,
					)}
					data-flx="shop.shop-checkout-modal.closed"
				/>
			);
			footer = closeButton;
			break;
		case 'busy':
			body = (
				<StatusView
					icon={warningIcon}
					title={i18n._(TOO_MANY_ORDERS_DESCRIPTOR)}
					body={
						checkout.failureMessage ??
						(checkout.waitMinutes === null
							? i18n._(TRY_AGAIN_IN_A_FEW_SECONDS_DESCRIPTOR)
							: i18n._(RATE_LIMITED_WAIT_DESCRIPTOR, {
									duration: i18n._(MINUTES_DURATION_PLURAL_DESCRIPTOR, {minutes: checkout.waitMinutes}),
								}))
					}
					data-flx="shop.shop-checkout-modal.busy"
				/>
			);
			footer = (
				<>
					{closeButton}
					<Button onClick={() => checkout.retry()} data-flx="shop.shop-checkout-modal.button.retry--2">
						{i18n._(TRY_AGAIN_DESCRIPTOR)}
					</Button>
				</>
			);
			break;
		case 'already_owned':
			body = (
				<StatusView
					icon={warningIcon}
					title={i18n._(
						target.kind === 'bundle'
							? BUNDLE_ITEM_OWNED_DESCRIPTOR
							: target.kind === 'username'
								? NAME_IS_ALREADY_YOURS_DESCRIPTOR
								: ALREADY_OWNED_DESCRIPTOR,
					)}
					data-flx="shop.shop-checkout-modal.already-owned"
				/>
			);
			// A bundle has nothing to equip: the server refuses it whole. A name is worn by having it.
			footer = (
				<>
					{closeButton}
					{target.kind !== 'bundle' && target.kind !== 'username' && (
						<Button
							onClick={() => void checkout.equip()}
							submitting={checkout.checking}
							data-flx="shop.shop-checkout-modal.button.equip"
						>
							{i18n._(EQUIP_DESCRIPTOR)}
						</Button>
					)}
				</>
			);
			break;
	}
	return (
		<Modal.Root
			size={target.kind === 'premium' ? 'large' : recurringBoost ? 'small' : 'medium'}
			centered
			data-flx="shop.shop-checkout-modal.modal-root"
		>
			<Modal.Header title={title} data-flx="shop.shop-checkout-modal.modal-header" />
			<Modal.Content data-flx="shop.shop-checkout-modal.content">
				<AnimatePresence mode="wait" initial={false} data-flx="shop.shop-checkout-modal.animate-presence">
					<motion.div
						key={view}
						initial={reducedMotion ? false : {opacity: 0, transform: 'translateY(4px)'}}
						animate={{
							opacity: 1,
							transform: 'translateY(0px)',
							transition: {duration: reducedMotion ? 0 : 0.18, ease: EASE_OUT},
						}}
						exit={reducedMotion ? {opacity: 1} : {opacity: 0, transition: {duration: 0.12, ease: EASE_OUT}}}
						data-flx="shop.shop-checkout-modal.view"
					>
						{body}
					</motion.div>
				</AnimatePresence>
			</Modal.Content>
			<Modal.Footer data-flx="shop.shop-checkout-modal.modal-footer">{footer}</Modal.Footer>
		</Modal.Root>
	);
});
